import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Tabs,
  TabsHeader,
  TabsBody,
  Tab,
  TabPanel,
  Collapse,
} from "@material-tailwind/react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Label from "components/Label/Label";

interface OwnerDetailsPageProps {
  open: boolean;
  handleClose?: () => void;
  ownerData: {
    id?: number;
    name?: string;
    lastName?: string;
    dateOfBirth?: string;
    phone?: string;
    validation?: string;
    enable?: boolean;
    account_id?: string;
    country?: string;
    city?: string;
    line1?: string;
    rue?: string;
    postal_code?: string;
    account_type?: string;
    rib?: string;
    bank?: string;
    companyName?: string;
    companySiren?: string;
    companyPhone?: string;
    companyEmail?: string;
    owner_title?: string;
    owner_country?: string;
    owner_city?: string;
    owner_line1?: string;
    owner_rue?: string;
    owner_postal_code?: string;
    createdAt?: string;
    updatedAt?: string;
    userId?: number;
    User?: {
      id?: number;
      email?: string;
      role?: string;
      last_connection?: string | null;
      emailVirefied?: boolean;
      enable?: boolean;
      subscription_status?: string;
      createdAt?: string;
    };
  } | null;
}

const OwnerDetailsPage: React.FC<OwnerDetailsPageProps> = ({ open, handleClose, ownerData }) => {
  const [activeTab, setActiveTab] = useState("basic");
  const [openCollapse, setOpenCollapse] = useState({
    address: false,
    company: false,
  });

  // Toggle collapse sections
  const toggleCollapse = (section: keyof typeof openCollapse) => {
    setOpenCollapse((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Field icons using Line Awesome
  const fieldIcons = {
    name: "las la-user",
    lastName: "las la-user",
    dateOfBirth: "las la-birthday-cake",
    phone: "las la-phone",
    email: "las la-at",
    createdAt: "las la-calendar-plus",
    last_connection: "las la-clock",
    validation: "las la-check-circle",
    enable: "las la-toggle-on",
    subscription_status: "las la-credit-card",
    role: "las la-user-shield",
    id: "las la-id-badge",
    userId: "las la-user",
    address: "las la-map-marker-alt",
    emailVirefied: "las la-check-circle",
    updatedAt: "las la-calendar-check",
    account_type: "las la-briefcase",
    rib: "las la-file-invoice",
    bank: "las la-university",
    companyName: "las la-building",
    companySiren: "las la-id-card",
    companyPhone: "las la-phone",
    companyEmail: "las la-envelope",
    owner_title: "las la-user-tie",
  };

  // Tab data
  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Contact", value: "contact" },
    { label: "Détails avancés", value: "advanced" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => console.log}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Propriétaire
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "Nom", value: ownerData?.name, icon: fieldIcons.name },
                { label: "Prénom", value: ownerData?.lastName, icon: fieldIcons.lastName },
                {
                  label: "Date de naissance",
                  value: ownerData?.dateOfBirth
                    ? new Date(ownerData.dateOfBirth).toLocaleDateString("fr-FR")
                    : "N/A",
                  icon: fieldIcons.dateOfBirth,
                },
                { label: "Type de compte", value: ownerData?.account_type, icon: fieldIcons.account_type },
                {
                  label: "Statut de validation",
                  value: ownerData?.validation || "N/A",
                  icon: fieldIcons.validation,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}

              {/* Collapsible Address */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("address")}
                >
                  <i className={fieldIcons.address}></i>
                  Adresse {openCollapse.address ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.address}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    <Typography className="text-gray-700 dark:text-gray-300">
                      {ownerData?.line1 ||
                      ownerData?.rue ||
                      ownerData?.city ||
                      ownerData?.country ||
                      ownerData?.postal_code
                        ? `${ownerData.line1 || ""} ${ownerData.rue || ""}, ${ownerData.city || ""}, ${ownerData.country || ""} ${ownerData.postal_code || ""}`.trim()
                        : "Aucune information d'adresse disponible."}
                    </Typography>
                  </div>
                </Collapse>
              </div>

              {/* Collapsible Company Info */}
              <div className="mt-4">
                <Button
                  variant="text"
                  className="flex items-center gap-2 text-blue-600 dark:text-blue-400"
                  onClick={() => toggleCollapse("company")}
                >
                  <i className={fieldIcons.companyName}></i>
                  Informations de l'entreprise {openCollapse.company ? "▲" : "▼"}
                </Button>
                <Collapse open={openCollapse.company}>
                  <div className="p-4 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800">
                    {[
                      {
                        label: "Nom de l'entreprise",
                        value: ownerData?.companyName,
                        icon: fieldIcons.companyName,
                      },
                      { label: "SIREN", value: ownerData?.companySiren, icon: fieldIcons.companySiren },
                      { label: "Téléphone", value: ownerData?.companyPhone, icon: fieldIcons.companyPhone },
                      { label: "Email", value: ownerData?.companyEmail, icon: fieldIcons.companyEmail },
                    ].map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2">
                        <div className="flex items-center space-x-2">
                          <i className={`${item.icon} text-lg text-blue-500 dark:text-blue-400`}></i>
                          <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                        </div>
                        <Typography className="font-medium text-gray-900 dark:text-white">
                          {item.value || "N/A"}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            </TabPanel>

            {/* Contact Information Tab */}
            <TabPanel value="contact" className="space-y-4">
              {[
                { label: "Numéro de téléphone", value: ownerData?.phone, icon: fieldIcons.phone },
                { label: "Email", value: ownerData?.User?.email, icon: fieldIcons.email },
                {
                  label: "Dernière connexion",
                  value: ownerData?.User?.last_connection
                    ? new Date(ownerData.User.last_connection).toLocaleDateString("fr-FR", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      })
                    : "N/A",
                  icon: fieldIcons.last_connection,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>

            {/* Advanced Details Tab */}
            <TabPanel value="advanced" className="space-y-4">
              {[
                { label: "ID du propriétaire", value: ownerData?.id, icon: fieldIcons.id },
                { label: "ID de l'utilisateur", value: ownerData?.userId, icon: fieldIcons.userId },
                { label: "Rôle", value: ownerData?.User?.role, icon: fieldIcons.role },
                {
                  label: "Statut de l'abonnement",
                  value: ownerData?.User?.subscription_status,
                  icon: fieldIcons.subscription_status,
                },
                {
                  label: "Email vérifié",
                  value: ownerData?.User?.emailVirefied ? "Oui" : "Non",
                  icon: fieldIcons.emailVirefied,
                },
                { label: "Compte activé", value: ownerData?.enable ? "Oui" : "Non", icon: fieldIcons.enable },
                {
                  label: "Inscrit le",
                  value: ownerData?.createdAt ? new Date(ownerData.createdAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.createdAt,
                },
                {
                  label: "Mis à jour le",
                  value: ownerData?.updatedAt ? new Date(ownerData.updatedAt).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.updatedAt,
                },
                { label: "RIB", value: ownerData?.rib, icon: fieldIcons.rib },
                { label: "Banque", value: ownerData?.bank || "N/A", icon: fieldIcons.bank },
                { label: "Titre du propriétaire", value: ownerData?.owner_title, icon: fieldIcons.owner_title },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default OwnerDetailsPage;
